import { mutate } from 'swr';
import { stringify } from 'query-string';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { usePageSellerId } from '@dashboard/hooks/usePage';
import { apiUrl } from '@dashboard/services/env';
import logException from '@dashboard/services/logException';
import request, { fetcher, fetchInfinite } from '@dashboard/services/request';

export const updateProduct = async (values, productId, sellerId, token) => {
  try {
    const id = productId || (values && values._id);
    const key = id ? `/sellers/${sellerId}/products/${id}` : `/sellers/${sellerId}/products`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    // mutate existing product or add new
    mutate(`/products/${id ? id : response.data._id}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save product', data: null };
  }
};

async function createStripePrice(productId, sellerId, token) {
  try {
    const response = await request(
      apiUrl + `/sellers/${sellerId}/products/${productId}/create-stripe-price`,
      token,
      {
        method: 'POST',
      },
    );

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    mutate(`/products/${productId}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to create stripe price', error });
    if (error && error.message) {
      return { error: error.message };
    }

    return {
      error: 'Unable to create subscription plan in Stripe',
      data: null,
    };
  }
}

export const saveVariant = async (values, variantId, productId, sellerId, token) => {
  try {
    const key = variantId
      ? `/sellers/${sellerId}/products/${productId}/variants/${variantId}`
      : `/sellers/${sellerId}/products/${productId}/variants`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save variant', data: null };
  }
};

export const updateProductMutation = async (values, productId, sellerId, token) => {
  const id = productId || (values && values._id);
  const key = id ? `/sellers/${sellerId}/products/${id}` : `/sellers/${sellerId}/products`;

  return fetcher(key, token, {
    method: 'POST',
    body: JSON.stringify(values),
  });
};

export const deleteProduct = async (productId, sellerId, token, { fromAllPages = false } = {}) => {
  const query = fromAllPages ? '?fromAllPages=true' : '';
  return fetcher(`/sellers/${sellerId}/products/${productId}${query}`, token, {
    method: 'DELETE',
  });
};

export const copyProduct = async (productId, values, sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/products/${productId}/copy`, token, {
    method: 'POST',
    body: JSON.stringify(values ?? {}),
  });
};

export const attachProductFile = async (productId, fileId, sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/products/${productId}/files`, token, {
    method: 'POST',
    body: JSON.stringify({ fileId }),
  });
};

export const detachProductFile = async (productId, fileId, sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/products/${productId}/files/${fileId}`, token, {
    method: 'DELETE',
  });
};

export const attachProductMedia = async (productId, fileId, sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/products/${productId}/media`, token, {
    method: 'POST',
    body: JSON.stringify({ fileId }),
  });
};

export const detachProductMedia = async (productId, fileId, sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/products/${productId}/media/${fileId}`, token, {
    method: 'DELETE',
  });
};

export const getProducts = async (sellerId, token, query = {}) => {
  return fetchInfinite(`/sellers/${sellerId}/products?${stringify(query)}`, token);
};

export const getProduct = async (productId, sellerId, token) =>
  fetcher(`/sellers/${sellerId}/products/${productId}`, token);

export default function useProductQueries() {
  const { token } = useAuth();
  const sellerId = usePageSellerId();

  return {
    updateProduct: (values, productId) => updateProduct(values, productId, sellerId, token),
    updateProductMutation: (values, productId) =>
      updateProductMutation(values, productId, sellerId, token),
    attachProductFile: (productId, fileId) => attachProductFile(productId, fileId, sellerId, token),
    copyProduct: (productId, values) => copyProduct(productId, values, sellerId, token),
    deleteProduct: (productId, options) => deleteProduct(productId, sellerId, token, options),
    detachProductFile: (productId, fileId) => detachProductFile(productId, fileId, sellerId, token),
    attachProductMedia: (productId, fileId) =>
      attachProductMedia(productId, fileId, sellerId, token),
    detachProductMedia: (productId, fileId) =>
      detachProductMedia(productId, fileId, sellerId, token),
    createStripePrice: productId => createStripePrice(productId, sellerId, token),
    saveVariant: (values, variantId, productId) =>
      saveVariant(values, variantId, productId, sellerId, token),
    getProducts: (sellerId, query) => getProducts(sellerId, token, query),
    getProduct: productId => getProduct(productId, sellerId, token),
  };
}
